import { useState, type FormEvent } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import toast from "react-hot-toast";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { useAgents } from "@/components/Agents/AgentsContext";
import {
  CONNECTION_KIND_OPTIONS,
  type ConnectionKind,
} from "@/lib/agentsDemoData";
import { agentsHref } from "@/lib/atwAgentsHost";

export function CreateConnectionPage() {
  const { may, viewOnly, addConnection } = useAgents();
  const navigate = useNavigate();
  const [name, setName] = useState("");
  const [kind, setKind] = useState<ConnectionKind>("atw");
  const [mail, setMail] = useState(false);
  const canCreate = may("manage_connections");

  const submit = (event: FormEvent) => {
    event.preventDefault();
    if (!canCreate) return;
    const id = addConnection({ name, kind, mail });
    if (!id) {
      toast.error("Enter a connection name.");
      return;
    }
    toast.success(`${name.trim()} added.`);
    navigate(agentsHref("/connections"));
  };

  return (
    <div className="space-y-6">
      <div>
        <Link
          to={agentsHref("/connections")}
          className="mb-2 inline-flex items-center gap-1 text-sm text-gray-600 hover:underline dark:text-gray-400"
        >
          <ArrowLeft className="size-4" aria-hidden />
          Connections
        </Link>
        <h1 className="text-2xl font-semibold text-gray-900 dark:text-white">Add a connection</h1>
        <p className="mt-1 max-w-2xl text-sm text-gray-600 dark:text-gray-400">
          Name the connection and choose a type. Saving it here keeps it on this organization. It
          stays not connected until a later connect step.
        </p>
      </div>

      {viewOnly ? (
        <p className="max-w-2xl rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-950 dark:border-amber-900/40 dark:bg-amber-950/30 dark:text-amber-100">
          Standard role is view only. Ask a Super Admin to add a connection.
        </p>
      ) : null}

      <Card className="max-w-2xl">
        <CardHeader>
          <CardTitle className="text-base">New connection</CardTitle>
          <CardDescription>Choose ATW, OAuth, API, or MCP. Credentials are not collected yet.</CardDescription>
        </CardHeader>
        <CardContent>
          <form className="space-y-4" onSubmit={submit}>
            <label className="block text-sm">
              <span className="font-medium text-gray-700 dark:text-gray-300">Connection name</span>
              <Input
                className="mt-1 w-full"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="e.g. Company mail"
                disabled={!canCreate}
                autoComplete="off"
              />
            </label>
            <label className="block text-sm">
              <span className="font-medium text-gray-700 dark:text-gray-300">Type</span>
              <select
                className="mt-1 w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm disabled:opacity-50 dark:border-white/20 dark:bg-[#12161d] dark:text-white"
                value={kind}
                onChange={(e) => setKind(e.target.value as ConnectionKind)}
                disabled={!canCreate}
              >
                {CONNECTION_KIND_OPTIONS.map((option) => (
                  <option key={option.id} value={option.id}>
                    {option.label}
                  </option>
                ))}
              </select>
            </label>
            <label className="flex items-start gap-2 text-sm">
              <input
                type="checkbox"
                className="mt-1"
                checked={mail}
                onChange={(e) => setMail(e.target.checked)}
                disabled={!canCreate}
              />
              <span>
                <span className="font-medium text-gray-700 dark:text-gray-300">
                  This is a mail connection
                </span>
                <span className="mt-0.5 block text-gray-600 dark:text-gray-400">
                  ATW Mail, Google, or Microsoft. The address an agent sends as is chosen on the
                  agent, after this connection exists.
                </span>
              </span>
            </label>

            <div className="flex flex-wrap justify-end gap-2 pt-2">
              <Button type="button" variant="outline" onClick={() => navigate(agentsHref("/connections"))}>
                Cancel
              </Button>
              <Button type="submit" disabled={!canCreate}>
                Add connection
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
